// ═══════════════════════════════════════════════════════════════
// PACE — Reports
// Compliance summaries, tier coverage, CSV/PDF export
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import {
  Paper, Box, Group, Stack, SimpleGrid, Title, Text, Button, Progress,
  Table, Center, Loader,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconDownload, IconFileText, IconPrinter, IconRefresh,
  IconWand } from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { usePaceStore, PACE_TIERS, PACE_TIER_LABELS } from '../../store/paceStore';
import type { PaceTier, PaceProfile } from '../../store/paceStore';
import { exportComplianceReport, exportContactSheet } from '../../lib/paceExportPdf';

interface ComplianceSummary {
  totalProfiles: number;
  compliantProfiles: number;
  compliancePercent: number;
  staleProfiles: number;
  byTier: { tier: PaceTier; count: number; percent: number }[];
  byDepartment: { department: string; total: number; compliant: number; percent: number }[];
}

export default function Reports() {
  const { activeIncident } = useAuthStore();
  const { profiles, loading, loadProfiles, runGapAnalysis } = usePaceStore();
  const [compliance, setCompliance] = useState<ComplianceSummary | null>(null);
  const [aiSummary, setAiSummary] = useState('');
  const [loadingAi, setLoadingAi] = useState(false);

  useEffect(() => { loadProfiles(); }, []);

  useEffect(() => {
    if (profiles.length > 0) {
      setCompliance(buildCompliance(profiles));
    }
  }, [profiles]);

  const buildCompliance = (profileList: PaceProfile[]): ComplianceSummary => {
    const personTiers = new Map<string, Set<string>>();
    const personDepts = new Map<string, string>();
    profileList.forEach(p => {
      if (p.contactValue) {
        if (!personTiers.has(p.personEmail)) personTiers.set(p.personEmail, new Set());
        personTiers.get(p.personEmail)!.add(p.tier);
      }
      if (p.department) personDepts.set(p.personEmail, p.department);
    });

    const totalProfiles = personTiers.size;
    const compliantProfiles = [...personTiers.values()].filter(tiers => PACE_TIERS.every(t => tiers.has(t))).length;

    const staleProfiles = profileList.filter(p => {
      if (!p.lastVerified) return true;
      return Date.now() - new Date(p.lastVerified).getTime() > 180 * 86400000;
    }).length;

    const byTier = PACE_TIERS.map(tier => {
      const count = [...personTiers.values()].filter(tiers => tiers.has(tier)).length;
      return { tier, count, percent: totalProfiles > 0 ? Math.round((count / totalProfiles) * 100) : 0 };
    });

    // By department
    const deptMap = new Map<string, { total: Set<string>; compliant: Set<string> }>();
    for (const [email, tiers] of personTiers) {
      const dept = personDepts.get(email) || 'Unassigned';
      if (!deptMap.has(dept)) deptMap.set(dept, { total: new Set(), compliant: new Set() });
      deptMap.get(dept)!.total.add(email);
      if (PACE_TIERS.every(t => tiers.has(t))) deptMap.get(dept)!.compliant.add(email);
    }
    const byDepartment = [...deptMap.entries()].map(([dept, data]) => ({
      department: dept,
      total: data.total.size,
      compliant: data.compliant.size,
      percent: data.total.size > 0 ? Math.round((data.compliant.size / data.total.size) * 100) : 0,
    }));

    return {
      totalProfiles,
      compliantProfiles,
      compliancePercent: totalProfiles > 0 ? Math.round((compliantProfiles / totalProfiles) * 100) : 0,
      staleProfiles,
      byTier,
      byDepartment,
    };
  };

  const handleAiSummary = async () => {
    setLoadingAi(true);
    try {
      const text = await runGapAnalysis(profiles);
      setAiSummary(text);
    } catch (err: any) {
      notifications.show({ message: err?.message || 'AI analysis failed', color: 'red' });
    } finally { setLoadingAi(false); }
  };

  const exportCSV = () => {
    if (!compliance) return;
    const rows = [
      ['Department', 'Total Members', 'Compliant', 'Compliance %'],
      ...compliance.byDepartment.map(d => [d.department, d.total, d.compliant, d.percent + '%']),
      [],
      ['PACE Tier', 'Members with Data', 'Coverage %'],
      ...compliance.byTier.map(t => [PACE_TIER_LABELS[t.tier], t.count, t.percent + '%']),
    ];
    const csv = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `pace-compliance-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    notifications.show({ message: 'CSV exported!', color: 'green' });
  };

  const tierColorMap: Record<string, string> = {
    primary: 'green', alternate: 'blue', contingent: 'yellow', emergency: 'red',
  };

  if (loading && profiles.length === 0) {
    return <Center py="xl" mih={256}><Loader /></Center>;
  }

  return (
    <Box>
      <Group justify="space-between" mb="lg">
        <Box>
          <Title order={3} c="black">Compliance Reports</Title>
          <Text c="dimmed" size="sm" mt={4}>Generated {new Date().toLocaleDateString()}</Text>
        </Box>
        <Group gap="xs">
          <Button variant="outline" size="sm" onClick={() => loadProfiles()} leftSection={<IconRefresh size={12} />}>
            Refresh
          </Button>
          <Button size="sm" onClick={exportCSV} leftSection={<IconDownload size={12} />}>CSV</Button>
          <Button size="sm" color="red"
            onClick={() => compliance && exportComplianceReport(activeIncident?.name ?? 'Incident', compliance)}
            disabled={!compliance} leftSection={<IconFileText size={12} />}>
            PDF
          </Button>
          <Button size="sm" color="gray"
            onClick={() => profiles.length > 0 && exportContactSheet(activeIncident?.name ?? 'Incident', profiles)}
            disabled={profiles.length === 0} leftSection={<IconPrinter size={12} />}>
            Contact Sheet
          </Button>
        </Group>
      </Group>

      {compliance && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
            {[
              { label: 'Total Profiles', value: compliance.totalProfiles, color: 'gray', icon: 'users' },
              { label: 'Compliant', value: compliance.compliantProfiles, color: 'green', icon: 'check' },
              { label: 'Compliance %', value: `${compliance.compliancePercent}%`, color: 'blue', icon: 'chart' },
              { label: 'Stale Records', value: compliance.staleProfiles, color: 'yellow', icon: 'warn' },
            ].map(s => (
              <Paper key={s.label} p="sm" withBorder ta="center" style={{ borderTop: `3px solid var(--mantine-color-${s.color}-5)` }}>
                <Text size="xs" fw={500} tt="uppercase" c="dimmed">{s.label}</Text>
                <Text fz={28} fw={700} mt={2} c={s.color === 'gray' ? undefined : s.color}>{s.value}</Text>
              </Paper>
            ))}
          </SimpleGrid>

          <Paper p="lg" withBorder mb="md">
            <Text fw={600} mb="md">Coverage by PACE Tier</Text>
            <Stack gap="sm">
              {compliance.byTier.map(({ tier, count, percent }) => (
                <Box key={tier}>
                  <Group justify="space-between" mb={4}>
                    <Text size="sm" fw={500}>{PACE_TIER_LABELS[tier]}</Text>
                    <Text size="sm" c="dimmed">{count} / {compliance.totalProfiles} ({percent}%)</Text>
                  </Group>
                  <Progress value={percent} color={tierColorMap[tier]} size="sm" radius="xl" />
                </Box>
              ))}
            </Stack>
          </Paper>

          {compliance.byDepartment.length > 0 && (
            <Paper p="lg" withBorder mb="md">
              <Text fw={600} mb="md">Compliance by Department</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Department</Table.Th>
                    <Table.Th>Members</Table.Th>
                    <Table.Th>Compliant</Table.Th>
                    <Table.Th>Rate</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {compliance.byDepartment.map(d => (
                    <Table.Tr key={d.department}>
                      <Table.Td fw={500}>{d.department}</Table.Td>
                      <Table.Td>{d.total}</Table.Td>
                      <Table.Td>{d.compliant}</Table.Td>
                      <Table.Td>
                        <Text fw={600} size="sm"
                          c={d.percent >= 80 ? 'green' : d.percent >= 50 ? 'yellow.7' : 'red'}>
                          {d.percent}%
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          <Paper p="lg" withBorder>
            <Group justify="space-between" mb="sm">
              <Group gap="xs">
                <IconWand size={14} color="var(--mantine-color-violet-5)" />
                <Text fw={600}>AI Readiness Analysis</Text>
              </Group>
              <Button size="xs" color="violet" onClick={handleAiSummary} loading={loadingAi}>
                {loadingAi ? 'Analyzing...' : 'Run Analysis'}
              </Button>
            </Group>
            {aiSummary ? (
              <Text size="sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{aiSummary}</Text>
            ) : (
              <Text size="sm" c="dimmed" fs="italic">
                Run AI analysis to get a gap report, single points of failure, and actionable recommendations.
              </Text>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
}
